import { clientConfig } from "../config";
import type { TokenSet } from "./types";

const TOKENS_KEY = "sonox.im.tokens.v1";
const TRANSACTION_KEY = "sonox.im.oidc.transaction.v1";

interface OidcTransaction {
  readonly state: string;
  readonly verifier: string;
  readonly nonce: string;
  readonly redirectUri: string;
}

interface TokenResponse {
  readonly access_token?: string;
  readonly refresh_token?: string;
  readonly id_token?: string;
  readonly expires_in?: number;
  readonly error?: string;
  readonly error_description?: string;
}

export interface PasswordLoginInput {
  readonly email: string;
  readonly password: string;
}

export interface RegistrationInput extends PasswordLoginInput {
  readonly name: string;
}

export function hasAuthorizationCallback(): boolean {
  const url = new URL(window.location.href);
  return url.searchParams.has("code") || url.searchParams.has("error");
}

/** Starts Authorization Code + PKCE. First-time SSO login auto-registers IM. */
export async function beginAuthorization(): Promise<never> {
  const redirectUri = `${window.location.origin}/callback`;
  const transaction: OidcTransaction = {
    state: randomBase64Url(24),
    verifier: randomBase64Url(48),
    nonce: randomBase64Url(24),
    redirectUri,
  };
  sessionStorage.setItem(TRANSACTION_KEY, JSON.stringify(transaction));
  const authorizeUrl = new URL("/oauth/authorize", clientConfig.oidcIssuer);
  authorizeUrl.searchParams.set("client_id", clientConfig.oidcClientId);
  authorizeUrl.searchParams.set("redirect_uri", redirectUri);
  authorizeUrl.searchParams.set("response_type", "code");
  authorizeUrl.searchParams.set("scope", "openid profile email offline_access");
  authorizeUrl.searchParams.set("state", transaction.state);
  authorizeUrl.searchParams.set("nonce", transaction.nonce);
  authorizeUrl.searchParams.set("code_challenge", await sha256Base64Url(transaction.verifier));
  authorizeUrl.searchParams.set("code_challenge_method", "S256");
  window.location.assign(authorizeUrl);
  return new Promise<never>(() => undefined);
}

export async function completeAuthorization(): Promise<TokenSet> {
  const callbackUrl = new URL(window.location.href);
  const error = callbackUrl.searchParams.get("error");
  if (error !== null) {
    throw new Error(callbackUrl.searchParams.get("error_description") ?? error);
  }
  const transaction = parseTransaction(sessionStorage.getItem(TRANSACTION_KEY));
  const code = callbackUrl.searchParams.get("code");
  if (code === null || callbackUrl.searchParams.get("state") !== transaction.state) {
    throw new Error("登录回调校验失败，请重新登录");
  }
  const body = new URLSearchParams({
    grant_type: "authorization_code",
    code,
    redirect_uri: transaction.redirectUri,
    client_id: clientConfig.oidcClientId,
    code_verifier: transaction.verifier,
  });
  const response = await fetch(new URL("/oauth/token", clientConfig.oidcIssuer), {
    method: "POST",
    headers: { "content-type": "application/x-www-form-urlencoded" },
    body,
  });
  const tokens = await parseTokenResponse(response);
  saveTokens(tokens);
  sessionStorage.removeItem(TRANSACTION_KEY);
  window.history.replaceState({}, "", "/");
  return tokens;
}

/** Authenticates a first-party Sonox account through the central SSO service. */
export async function loginWithPassword(input: PasswordLoginInput): Promise<TokenSet> {
  return localAuthRequest("/auth/login", input);
}

/** Registers a first-party account and returns the same token set as OIDC login. */
export async function registerWithPassword(input: RegistrationInput): Promise<TokenSet> {
  return localAuthRequest("/auth/register", input);
}

export function loadTokens(): TokenSet | null {
  const value = sessionStorage.getItem(TOKENS_KEY);
  if (value === null) return null;
  try {
    const parsed = JSON.parse(value) as Partial<TokenSet>;
    return typeof parsed.accessToken === "string" && typeof parsed.expiresAt === "number"
      ? parsed as TokenSet
      : null;
  } catch {
    return null;
  }
}

/** Returns a fresh access token, rotating the public-client refresh token. */
export async function accessToken(): Promise<string> {
  const current = loadTokens();
  if (current === null) throw new Error("尚未登录");
  if (current.expiresAt > Date.now() + 60_000) return current.accessToken;
  if (current.refreshToken === undefined) {
    clearTokens();
    throw new Error("登录已过期，请重新登录");
  }
  const response = await fetch(new URL("/oauth/token", clientConfig.oidcIssuer), {
    method: "POST",
    headers: { "content-type": "application/x-www-form-urlencoded" },
    body: new URLSearchParams({
      grant_type: "refresh_token",
      refresh_token: current.refreshToken,
      client_id: clientConfig.oidcClientId,
    }),
  });
  const refreshed = await parseTokenResponse(response);
  saveTokens(refreshed);
  return refreshed.accessToken;
}

export async function logoutFromSso(): Promise<void> {
  const current = loadTokens();
  if (current?.refreshToken !== undefined) {
    await fetch(new URL("/oauth/revoke", clientConfig.oidcIssuer), {
      method: "POST",
      headers: { "content-type": "application/x-www-form-urlencoded" },
      body: new URLSearchParams({
        token: current.refreshToken,
        client_id: clientConfig.oidcClientId,
      }),
    }).catch(() => undefined);
  }
  clearTokens();
}

export function clearTokens(): void {
  sessionStorage.removeItem(TOKENS_KEY);
  sessionStorage.removeItem(TRANSACTION_KEY);
}

function saveTokens(tokens: TokenSet): void {
  sessionStorage.setItem(TOKENS_KEY, JSON.stringify(tokens));
}

async function localAuthRequest(
  path: "/auth/login" | "/auth/register",
  input: PasswordLoginInput | RegistrationInput,
): Promise<TokenSet> {
  const response = await fetch(new URL(path, clientConfig.oidcIssuer), {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ client_id: clientConfig.oidcClientId, ...input }),
  });
  const tokens = await parseTokenResponse(response);
  saveTokens(tokens);
  return tokens;
}

async function parseTokenResponse(response: Response): Promise<TokenSet> {
  const body = await response.json() as TokenResponse;
  if (!response.ok || body.access_token === undefined) {
    throw new Error(body.error_description ?? body.error ?? "登录服务返回异常");
  }
  return {
    accessToken: body.access_token,
    ...(body.refresh_token === undefined ? {} : { refreshToken: body.refresh_token }),
    ...(body.id_token === undefined ? {} : { idToken: body.id_token }),
    expiresAt: Date.now() + Math.max(30, body.expires_in ?? 3600) * 1_000,
  };
}

function parseTransaction(value: string | null): OidcTransaction {
  if (value === null) throw new Error("登录事务已失效，请重新登录");
  try {
    const parsed = JSON.parse(value) as Partial<OidcTransaction>;
    if (
      typeof parsed.state !== "string" ||
      typeof parsed.verifier !== "string" ||
      typeof parsed.nonce !== "string" ||
      typeof parsed.redirectUri !== "string"
    ) {
      throw new TypeError("invalid transaction");
    }
    return parsed as OidcTransaction;
  } catch {
    throw new Error("登录事务已损坏，请重新登录");
  }
}

function randomBase64Url(bytes: number): string {
  const value = new Uint8Array(bytes);
  crypto.getRandomValues(value);
  return toBase64Url(value);
}

async function sha256Base64Url(value: string): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(value));
  return toBase64Url(new Uint8Array(digest));
}

function toBase64Url(value: Uint8Array): string {
  let binary = "";
  for (const byte of value) binary += String.fromCharCode(byte);
  return btoa(binary).replaceAll("+", "-").replaceAll("/", "_").replace(/=+$/u, "");
}
